import { ChangeEvent, SyntheticEvent, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import BootstrapForm from 'react-bootstrap/Form';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import log from 'common/ScalyrLogger';
import {
  getReadableTimeRange,
  getTimeDifferenceInMs,
  getTimeRangeText,
  isDuration,
  MS_PER_UNIT,
  parseDuration,
} from 'common/util/time';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import { hindsightRetentionState, retentionPeriodState } from 'components/account/account.state';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Link from 'components/common/Link/Link';
import TimeOptions from 'components/common/TimeOptions/TimeOptions';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import { defaultStartTime, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { isSearchPageResultsPendingSelector } from 'components/search/SearchLogsPage/SearchStateManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import { TimeRangeDropdownValidationStatus, useTimeRangeDropdownState } from './TimeRangeDropdown.hooks';

import './TimeRangeDropdown.scss';

const timeOptions = [
  '10 min',
  '1 hour',
  '24 hours',
  '20 min',
  '2 hours',
  '36 hours',
  '30 min',
  '4 hours',
  '48 hours',
  '45 min',
  '12 hours',
  '72 hours',
];

type TimeRangeDropdownProps = {
  className?: string;
  buttonVariant?: ButtonVariant;
  buttonSize?: 'sm' | 'lg' | undefined;
};

/**
 * This component allows the user to set the time range for the current search.
 *
 * @prop className        class name for the dropdown button
 * @prop buttonVariant    variant for the dropdown button
 *
 */
function TimeRangeDropdown({ className, buttonVariant, buttonSize }: TimeRangeDropdownProps): JSX.Element {
  const isNewSearch = useIsNewSearch();
  const setSearchParams = useSetSearchParams();
  const isSearchPageResultsPending = useRecoilValue(isSearchPageResultsPendingSelector);

  // start/end from the URL
  const paramsStartTime = useRecoilValue(searchParamState(K.URL_PARAM.START_TIME));
  const paramsEndTime = useRecoilValue(searchParamState(K.URL_PARAM.END_TIME));

  // summary of the date range used as button text
  const [timeRangeText, setTimeRangeText] = useState('');

  const {
    state: { showOverlay, errors: timeRangeErrors, startTime, endTime },
    resetState: resetTimeRangeDropdownState,
    setShowOverlay,
    handleInputValidation,
    setStartTime,
    setEndTime,
  } = useTimeRangeDropdownState();

  const startTimeInputRef = useRef<HTMLInputElement>(null);
  const defaultStart = useRecoilValue(defaultStartTime);
  const timezone = useRecoilValue(timezoneState);

  const isHindsightLabEnabled = useIsLabEnabled(K.LAB.HINDSIGHT);
  const retentionPeriod = useRecoilValue(retentionPeriodState);
  const hindsightRetention = useRecoilValue(hindsightRetentionState);
  const [isHindsightSearch, setIsHindsightSearch] = useState(false);

  // select text in the start time INPUT when dropdown is shown
  const prevShowOverlay = usePrevious(showOverlay);
  useConditionalEffect(() => {
    const startInput = startTimeInputRef?.current;
    if (startInput) {
      startInput.select();
    }
  }, showOverlay && !prevShowOverlay);

  // the URL parameters are authoritative, so sync the local state to the url whenever there is a new search
  useConditionalEffect(() => {
    const start = paramsStartTime || defaultStart;
    const end = paramsEndTime;
    // make sure start and end as they appear in inputs are in a friendly form (not epoch time)
    const { startTime: readableStartTime, endTime: readableEndTime } = getReadableTimeRange(
      {
        startTime: start,
        endTime: end,
      },
      timezone,
    );
    setStartTime({ query: start, display: readableStartTime });
    setEndTime({ query: end || '', display: readableEndTime || K.DEFAULT_SEARCH_END });
    setTimeRangeText(getTimeRangeText({ startTime: start, endTime: end }, timezone) || 'Invalid Range');
  }, isNewSearch);

  const runSearch = (start: string, end: string): void => {
    log.debug('runSearch : ' + start + ' - ' + end);
    const timeRange = {
      startTime: !isDuration(start) || parseDuration(start) !== parseDuration(defaultStart) ? start : '',
      endTime: String(end).toUpperCase() !== K.DEFAULT_SEARCH_END ? end : '',
    } as UrlParams;
    log.debug('TimeRangeDropdown set URL time range to ' + [timeRange.startTime, timeRange.endTime].join(' - '));
    setSearchParams(timeRange);

    // whenever we run a search with an empty string start or end, manually reset the local state to the default value
    // since the url param itself may not have changed
    if (start === '') {
      setStartTime(defaultStart);
    }
    if (end === '') {
      setEndTime(K.DEFAULT_SEARCH_END);
    }
  };

  // user has clicked on one of the preset times
  const handleTimeClick = (newStart: string): void => {
    resetTimeRangeDropdownState();
    setStartTime(newStart);
    runSearch(newStart, '');
    setTimeRangeText(getTimeRangeText({ startTime: newStart, endTime: '' }, timezone) || 'Invalid Range');
    setShowOverlay(false);
    log.debug('Set start time to: ' + newStart);
  };

  // user has pressed the Go button, or the Enter key in an input
  const handleSubmit = (ev: SyntheticEvent): void => {
    ev.preventDefault();

    const { status } = handleInputValidation();
    if (status === TimeRangeDropdownValidationStatus.ERROR) {
      log.debug("TimeRangeDropdown component: Search won't be issued, invalid input");
      return;
    }

    setTimeRangeText(
      getTimeRangeText({ startTime: startTime.query, endTime: endTime.query }, timezone) || 'Invalid Range',
    );
    runSearch(startTime.query, endTime.query);
  };

  // grid of preset relative time periods
  const timeOptionsSection = (
    <TimeOptions
      header="Search the last ..."
      options={timeOptions}
      clickHandler={handleTimeClick}
      testId={ELEMENT.searchTimeRangeInterval}
    />
  );

  // If Hindsight is enabled, check the start time to see if it will result in Hindsight charges, and warn the user.
  const checkHindsight = (start = '') => {
    if (isHindsightLabEnabled) {
      const rangeMs = getTimeDifferenceInMs(
        {
          startTime: start || defaultStart || K.DEFAULT_SEARCH_START,
          endTime: '',
        },
        timezone,
      );
      setIsHindsightSearch(hindsightRetention > retentionPeriod && rangeMs > retentionPeriod * MS_PER_UNIT[K.DATE.DAY]);
    }
  };

  const hindsightWarningEl = !isHindsightSearch ? null : (
    <div className="TimeRangeDropdown__HindsightWarningContainer">
      <div className="TimeRangeDropdown__HindsightWarning">
        <span>
          Note: The time range exceeds the standard retention period of {retentionPeriod} days. A search run with this
          time range will incur Hindsight costs.
        </span>
        <Link href={HELP_ROUTE.HELP_HINDSIGHT} className="link TimeRangeDropdown__HindsightHelp" openInNewTab>
          Help
        </Link>
      </div>
    </div>
  );

  // form where user can enter start/end times
  const fromToSection = (
    <div className="TimeRangeDropdown__FromTo">
      <div className="TimeRangeDropdown__Header uppercase">Custom</div>
      <BootstrapForm onSubmit={handleSubmit}>
        <div>
          <div className="TimeRangeInput__FromTo">
            <BootstrapForm.Group className="TimeRangeDropdown__InputGroup" controlId="TimeRangeDropdown__Input--start">
              <BootstrapForm.Label className="TimeRangeDropdown__InputLabel sr-only">From:</BootstrapForm.Label>
              <BootstrapForm.Control
                className="TimeRangeDropdown__Input form-control"
                required
                as="input"
                type="text"
                name="start"
                value={startTime.display}
                isInvalid={timeRangeErrors.startTime}
                autoFocus
                ref={startTimeInputRef}
                onChange={(ev): void => {
                  const startValue = (ev.target as HTMLInputElement).value;
                  setStartTime(startValue);
                }}
                onBlur={(ev: ChangeEvent): void => {
                  const startValue = (ev.target as HTMLInputElement).value;
                  checkHindsight(startValue);
                }}
                placeholder="Start"
                data-test-id={ELEMENT.searchTimeRangeStart}
              />
            </BootstrapForm.Group>
            <div className="TimeRangeInput__Connector">to</div>
            <BootstrapForm.Group className="TimeRangeDropdown__InputGroup" controlId="TimeRangeDropdown__Input--end">
              <BootstrapForm.Label className="TimeRangeDropdown__InputLabel sr-only">To:</BootstrapForm.Label>
              <BootstrapForm.Control
                className="TimeRangeDropdown__Input form-control"
                as="input"
                type="text"
                name="end"
                value={endTime.display}
                isInvalid={timeRangeErrors.endTime}
                onChange={(ev): void => {
                  const endValue = (ev.target as HTMLInputElement).value;
                  setEndTime(endValue);
                }}
                placeholder="End"
                data-test-id={ELEMENT.searchTimeRangeEnd}
              />
            </BootstrapForm.Group>
            <div className="TimeRangeDropdown__SubmitButton" />
            <Button
              type="submit"
              variant={ButtonVariant.PRIMARY}
              onClick={handleSubmit}
              testId={ELEMENT.searchTimeRangeGo}
            >
              Go
            </Button>
          </div>
        </div>
        {hindsightWarningEl}
        <div className="TimeRangeDropdown__Help">
          3h, 3 hours, 10:30 AM, Monday, etc.&nbsp;
          <Link
            href={HELP_ROUTE.HELP_TIME_REFERENCE}
            className="link"
            openInNewTab
            testId={ELEMENT.searchTimeRangeHelp}
          >
            More
          </Link>
        </div>
      </BootstrapForm>
    </div>
  );

  const dropdownBody = (
    <div className="Dropdown__Menu TimeRangeDropdown">
      {timeOptionsSection}
      {fromToSection}
    </div>
  );

  return (
    <div>
      <OverlayTrigger
        placement="bottom-end"
        overlay={dropdownBody}
        showOverlay={showOverlay}
        setShowOverlay={setShowOverlay}
        disabled={isSearchPageResultsPending}
      >
        <Button
          className={className}
          variant={buttonVariant}
          rightIcon={<ArrowDropDownIcon fontSize="small" />}
          testId={ELEMENT.searchTimeRange}
          disabled={isSearchPageResultsPending}
          size={buttonSize}
        >
          {timeRangeText}
        </Button>
      </OverlayTrigger>
    </div>
  );
}

export default TimeRangeDropdown;
